// Bullet-hole decals: small quads lying on the surface, in a fixed ring buffer (oldest overwritten).
// One instanced draw call. Alpha fades during the last seconds of the decal's life.

import * as THREE from "three";

const VERT = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iQuat;
attribute vec4 iData;
varying vec2 vUv;
varying float vAlpha;
varying float vSeed;
vec3 rotateByQuat(vec3 v, vec4 q) {
  vec3 t = 2.0 * cross(q.xyz, v);
  return v + q.w * t + cross(q.xyz, t);
}
void main() {
  vec3 local = vec3(position.xy * iData.x, 0.0);
  vec3 n = rotateByQuat(vec3(0.0, 0.0, 1.0), iQuat);
  vec3 world = iPos + rotateByQuat(local, iQuat) + n * 0.004;
  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  vUv = position.xy + 0.5;
  vAlpha = iData.y;
  vSeed = iData.z;
}
`;

const FRAG = /* glsl */ `
varying vec2 vUv;
varying float vAlpha;
varying float vSeed;
float hash(float n) { return fract(sin(n) * 43758.5453); }
void main() {
  vec2 q = (vUv - 0.5) * 2.0;
  float d = length(q);
  float ang = atan(q.y, q.x);
  float wob = 0.75 + 0.25 * hash(floor((ang + 3.14159) * 3.0) + vSeed * 31.0);
  float edge = d / wob;
  // A black hole, a ring of pale chipped surface around it and a faint soot halo: readable on both
  // dark and light walls at 20 m.
  float hole = 1.0 - smoothstep(0.12, 0.28, edge);
  float chip = (1.0 - smoothstep(0.28, 0.62, edge)) * (1.0 - hole);
  float soot = (1.0 - smoothstep(0.45, 1.0, edge)) * 0.5;
  vec3 col = mix(vec3(0.07, 0.065, 0.06), vec3(0.6, 0.57, 0.52), chip);
  col = mix(col, vec3(0.015), hole);
  float a = clamp(hole + chip * 0.8 + soot * (1.0 - chip) * 0.7, 0.0, 1.0) * vAlpha;
  gl_FragColor = vec4(col, a * 0.92);
  if (gl_FragColor.a < 0.01) discard;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const LIFE = 14;
const FADE = 4;

const _q = new THREE.Quaternion();
const _n = new THREE.Vector3();
const _z = new THREE.Vector3(0, 0, 1);
const _qs = new THREE.Quaternion();

export class DecalBatch {
  readonly mesh: THREE.Mesh;
  private readonly geometry = new THREE.InstancedBufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly iPos: THREE.InstancedBufferAttribute;
  private readonly iQuat: THREE.InstancedBufferAttribute;
  private readonly iData: THREE.InstancedBufferAttribute;
  private readonly age: Float32Array;
  private readonly capacity: number;
  private head = 0;
  private count = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.age = new Float32Array(capacity);
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3),
    );
    this.geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this.iPos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.iQuat = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.iData = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.iData.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("iPos", this.iPos);
    this.geometry.setAttribute("iQuat", this.iQuat);
    this.geometry.setAttribute("iData", this.iData);
    this.geometry.instanceCount = 0;
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      fog: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = "decals";
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    this.mesh.visible = false;
  }

  get active(): number {
    return this.count;
  }

  add(px: number, py: number, pz: number, nx: number, ny: number, nz: number, size: number): void {
    _n.set(nx, ny, nz);
    if (_n.lengthSq() < 1e-8) _n.set(0, 1, 0);
    _n.normalize();
    _q.setFromUnitVectors(_z, _n);
    // Random spin around the normal so holes do not look stamped.
    _qs.setFromAxisAngle(_n, Math.random() * Math.PI * 2);
    _q.premultiply(_qs);

    const slot = this.head;
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
    const p = this.iPos.array as Float32Array;
    p[slot * 3] = px;
    p[slot * 3 + 1] = py;
    p[slot * 3 + 2] = pz;
    const q = this.iQuat.array as Float32Array;
    q[slot * 4] = _q.x;
    q[slot * 4 + 1] = _q.y;
    q[slot * 4 + 2] = _q.z;
    q[slot * 4 + 3] = _q.w;
    const d = this.iData.array as Float32Array;
    d[slot * 4] = size;
    d[slot * 4 + 1] = 1;
    d[slot * 4 + 2] = Math.random();
    this.age[slot] = 0;
    this.iPos.needsUpdate = true;
    this.iQuat.needsUpdate = true;
    this.iData.needsUpdate = true;
    // Live slots are always the first `count` (the buffer fills before it wraps).
    this.geometry.instanceCount = this.count;
    this.mesh.visible = true;
  }

  clear(): void {
    this.count = 0;
    this.head = 0;
    this.geometry.instanceCount = 0;
    this.mesh.visible = false;
  }

  /** Ends every decal whose center lies in the box (the surface under it is gone). */
  removeInside(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void {
    const p = this.iPos.array as Float32Array;
    const d = this.iData.array as Float32Array;
    let changed = false;
    for (let i = 0; i < this.count; i++) {
      const x = p[i * 3];
      const y = p[i * 3 + 1];
      const z = p[i * 3 + 2];
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1 && z >= z0 && z <= z1 && this.age[i] < LIFE) {
        this.age[i] = LIFE;
        d[i * 4 + 1] = 0;
        changed = true;
      }
    }
    if (changed) this.iData.needsUpdate = true;
  }

  update(dt: number): void {
    if (this.count === 0) return;
    const d = this.iData.array as Float32Array;
    let live = 0;
    let changed = false;
    for (let i = 0; i < this.count; i++) {
      const a = this.age[i] + dt;
      this.age[i] = a;
      let alpha = 0;
      if (a < LIFE) {
        alpha = a > LIFE - FADE ? (LIFE - a) / FADE : 1;
        live++;
      }
      if (d[i * 4 + 1] !== alpha) {
        d[i * 4 + 1] = alpha;
        changed = true;
      }
    }
    if (changed) this.iData.needsUpdate = true;
    if (live === 0) this.clear();
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
